// This file was auto-generated by Fern from our API Definition.
import { normalizeClientOptionsWithAuth } from "../../../../BaseClient.js";
import { mergeHeaders } from "../../../../core/headers.js";
import * as core from "../../../../core/index.js";
import { toJson } from "../../../../core/json.js";
import * as environments from "../../../../environments.js";
import { handleNonStatusCodeError } from "../../../../errors/handleNonStatusCodeError.js";
import * as errors from "../../../../errors/index.js";
import * as AgentaApi from "../../../index.js";
/**
 * LLM applications — create, update, list, and delete apps.
 */
export class ApplicationsClient {
    _options;
    constructor(options) {
        this._options = normalizeClientOptionsWithAuth(options);
    }
    /**
     * List shared catalog types.
     *
     * Catalog types are reusable JSON-Schema building blocks referenced from
     * template schemas (for example `message`, `prompt-template`). Types are
     * read-only and version with the product.
     * See the [Applications guide](/reference/api-guide/applications#catalog).
     *
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @example
     *     await client.applications.listApplicationCatalogTypes()
     */
    listApplicationCatalogTypes(requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listApplicationCatalogTypes(requestOptions));
    }
    async __listApplicationCatalogTypes(requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/catalog/types/"),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            throw new errors.AgentaApiError({
                statusCode: _response.error.statusCode,
                body: _response.error.body,
                rawResponse: _response.rawResponse,
            });
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/catalog/types/");
    }
    /**
     * List application templates available in the catalog.
     *
     * Templates describe the handler (`uri`) and JSON schemas used to create
     * a new application. Pass `include_archived=true` to include retired
     * templates (useful when editing applications created from an old
     * template). Templates are global and read-only.
     *
     * @param {AgentaApi.ListApplicationCatalogTemplatesRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.listApplicationCatalogTemplates()
     */
    listApplicationCatalogTemplates(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listApplicationCatalogTemplates(request, requestOptions));
    }
    async __listApplicationCatalogTemplates(request = {}, requestOptions) {
        const { include_archived: includeArchived } = request;
        const _queryParams = {
            include_archived: includeArchived,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/catalog/templates/"),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/catalog/templates/");
    }
    /**
     * Fetch one application template by key.
     *
     * Use this to inspect the exact `uri`, `data`, and JSON Schemas for a
     * template before creating an application from it. `template_key` comes
     * from the `key` field of a template returned by the list endpoint
     * (for example `completion`, `chat`, `hook`).
     *
     * @param {AgentaApi.FetchApplicationCatalogTemplateRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.fetchApplicationCatalogTemplate({
     *         template_key: "template_key"
     *     })
     */
    fetchApplicationCatalogTemplate(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchApplicationCatalogTemplate(request, requestOptions));
    }
    async __fetchApplicationCatalogTemplate(request, requestOptions) {
        const { template_key: templateKey } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/catalog/templates/${core.url.encodePathParam(templateKey)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/catalog/templates/{template_key}");
    }
    /**
     * List presets scoped to a template.
     *
     * Presets are named parameter sets (for example a curated prompt +
     * model combination) that scaffold the first revision when creating an
     * application from a template. Pass `include_archived=true` to include
     * retired presets.
     *
     * @param {AgentaApi.ListApplicationCatalogPresetsRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.listApplicationCatalogPresets({
     *         template_key: "template_key"
     *     })
     */
    listApplicationCatalogPresets(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__listApplicationCatalogPresets(request, requestOptions));
    }
    async __listApplicationCatalogPresets(request, requestOptions) {
        const { template_key: templateKey, include_archived: includeArchived } = request;
        const _queryParams = {
            include_archived: includeArchived,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/catalog/templates/${core.url.encodePathParam(templateKey)}/presets/`),
            method: "GET",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/catalog/templates/{template_key}/presets/");
    }
    /**
     * Fetch one preset by key within a template.
     *
     * Returns the preset's `data` so clients can use it as the payload for a
     * first revision when creating an application from a template.
     *
     * @param {AgentaApi.FetchApplicationCatalogPresetRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.fetchApplicationCatalogPreset({
     *         template_key: "template_key",
     *         preset_key: "preset_key"
     *     })
     */
    fetchApplicationCatalogPreset(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchApplicationCatalogPreset(request, requestOptions));
    }
    async __fetchApplicationCatalogPreset(request, requestOptions) {
        const { template_key: templateKey, preset_key: presetKey } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/catalog/templates/${core.url.encodePathParam(templateKey)}/presets/${core.url.encodePathParam(presetKey)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/catalog/templates/{template_key}/presets/{preset_key}");
    }
    /**
     * Create an application artifact only.
     *
     * Returns an empty application without any variants or revisions.
     * Most callers should use `POST /simple/applications/` instead — it
     * creates the artifact, a default variant, and a first committed
     * revision in one request.
     * See the [Applications guide](/reference/api-guide/applications).
     *
     * @param {AgentaApi.ApplicationCreateRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.createApplication({
     *         application: {}
     *     })
     */
    createApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createApplication(request, requestOptions));
    }
    async __createApplication(request, requestOptions) {
        const { application_id: applicationId, ..._body } = request;
        const _queryParams = {
            application_id: applicationId,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/");
    }
    /**
     * Fetch one application artifact by ID.
     *
     * Returns artifact-level fields only. To get the current variant,
     * revision, and `data` in a single call, use
     * `GET /simple/applications/{application_id}`.
     *
     * @param {AgentaApi.FetchApplicationRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.fetchApplication({
     *         application_id: "application_id"
     *     })
     */
    fetchApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchApplication(request, requestOptions));
    }
    async __fetchApplication(request, requestOptions) {
        const { application_id: applicationId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/${core.url.encodePathParam(applicationId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/{application_id}");
    }
    /**
     * Edit artifact-level fields on an application.
     *
     * Editable fields: `description`, `flags`, `tags`, `meta`. Editing `name`
     * is currently disabled and returns `400`. Prompt or model-parameter
     * changes go through `POST /applications/revisions/commit`, not this
     * endpoint.
     *
     * @param {AgentaApi.ApplicationEditRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.editApplication({
     *         application_id: "application_id",
     *         application: {}
     *     })
     */
    editApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editApplication(request, requestOptions));
    }
    async __editApplication(request, requestOptions) {
        const { application_id: applicationId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/${core.url.encodePathParam(applicationId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/applications/{application_id}");
    }
    /**
     * Soft-delete an application.
     *
     * Archiving sets `deleted_at` on the application and hides it from
     * queries that don't set `include_archived: true`. Its variants and
     * revisions become unreachable from listing but their IDs remain
     * resolvable so historical traces stay intact.
     * See [Versioning](/reference/api-guide/versioning#archive-and-unarchive).
     *
     * @param {AgentaApi.ArchiveApplicationRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.archiveApplication({
     *         application_id: "application_id"
     *     })
     */
    archiveApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__archiveApplication(request, requestOptions));
    }
    async __archiveApplication(request, requestOptions) {
        const { application_id: applicationId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/${core.url.encodePathParam(applicationId)}/archive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/{application_id}/archive");
    }
    /**
     * Restore a previously archived application.
     *
     * Clears `deleted_at` and makes the application visible to standard
     * queries again. Safe to call on an already-active application; it is a
     * no-op in that case.
     *
     * @param {AgentaApi.UnarchiveApplicationRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.unarchiveApplication({
     *         application_id: "application_id"
     *     })
     */
    unarchiveApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__unarchiveApplication(request, requestOptions));
    }
    async __unarchiveApplication(request, requestOptions) {
        const { application_id: applicationId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/${core.url.encodePathParam(applicationId)}/unarchive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/{application_id}/unarchive");
    }
    /**
     * Query application artifacts.
     *
     * Returns only artifact-level fields; the variant, revision, and `data`
     * payload are not included. For one row per application with those
     * merged in, use `POST /simple/applications/query`.
     * See [Query Pattern](/reference/api-guide/query-pattern).
     *
     * @param {AgentaApi.ApplicationQueryRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.queryApplications()
     */
    queryApplications(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryApplications(request, requestOptions));
    }
    async __queryApplications(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/query");
    }
    /**
     * Create a new variant on an existing application.
     *
     * A variant is an independent branch of the application's history. The
     * new variant starts empty — call `POST /applications/revisions/commit`
     * to add its first revision. Use `POST /applications/variants/fork` when
     * you want the new variant to inherit an existing revision history.
     *
     * @param {AgentaApi.ApplicationVariantCreateRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.createApplicationVariant({
     *         application_variant: {}
     *     })
     */
    createApplicationVariant(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createApplicationVariant(request, requestOptions));
    }
    async __createApplicationVariant(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/variants/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/variants/");
    }
    /**
     * Fetch one variant by ID.
     *
     * Returns variant-level fields. To get the variant's tip revision and
     * its `data`, call `POST /applications/revisions/retrieve` with
     * `application_variant_ref`.
     *
     * @param {AgentaApi.FetchApplicationVariantRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.fetchApplicationVariant({
     *         application_variant_id: "application_variant_id"
     *     })
     */
    fetchApplicationVariant(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchApplicationVariant(request, requestOptions));
    }
    async __fetchApplicationVariant(request, requestOptions) {
        const { application_variant_id: applicationVariantId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/variants/${core.url.encodePathParam(applicationVariantId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/variants/{application_variant_id}");
    }
    /**
     * Edit a variant's header fields (`name`, `description`, `tags`, `meta`).
     *
     * Configuration changes go through a new commit via
     * `POST /applications/revisions/commit`. This endpoint only touches
     * variant-level metadata.
     *
     * @param {AgentaApi.ApplicationVariantEditRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.editApplicationVariant({
     *         application_variant_id: "application_variant_id",
     *         application_variant: {}
     *     })
     */
    editApplicationVariant(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editApplicationVariant(request, requestOptions));
    }
    async __editApplicationVariant(request, requestOptions) {
        const { application_variant_id: applicationVariantId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/variants/${core.url.encodePathParam(applicationVariantId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/applications/variants/{application_variant_id}");
    }
    /**
     * Soft-delete a variant.
     *
     * The variant and its revisions are hidden from queries unless
     * `include_archived: true` is sent. Revision IDs remain resolvable so
     * historical traces are preserved.
     *
     * @param {AgentaApi.ArchiveApplicationVariantRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.archiveApplicationVariant({
     *         application_variant_id: "application_variant_id"
     *     })
     */
    archiveApplicationVariant(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__archiveApplicationVariant(request, requestOptions));
    }
    async __archiveApplicationVariant(request, requestOptions) {
        const { application_variant_id: applicationVariantId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/variants/${core.url.encodePathParam(applicationVariantId)}/archive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/variants/{application_variant_id}/archive");
    }
    /**
     * Restore a previously archived variant.
     *
     * @param {AgentaApi.UnarchiveApplicationVariantRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.unarchiveApplicationVariant({
     *         application_variant_id: "application_variant_id"
     *     })
     */
    unarchiveApplicationVariant(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__unarchiveApplicationVariant(request, requestOptions));
    }
    async __unarchiveApplicationVariant(request, requestOptions) {
        const { application_variant_id: applicationVariantId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/variants/${core.url.encodePathParam(applicationVariantId)}/unarchive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/variants/{application_variant_id}/unarchive");
    }
    /**
     * Query variants across one or more applications.
     *
     * Filters are parsed from both query-string parameters and the request
     * body; body values take precedence. Use `application_refs` to scope to
     * specific applications, `application_variant_refs` to narrow to
     * specific variants.
     * See [Query Pattern](/reference/api-guide/query-pattern).
     *
     * @param {AgentaApi.QueryApplicationVariantsRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.queryApplicationVariants()
     */
    queryApplicationVariants(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryApplicationVariants(request, requestOptions));
    }
    async __queryApplicationVariants(request = {}, requestOptions) {
        const { application_id: applicationId, application_ids: applicationIds, application_slug: applicationSlug, application_slugs: applicationSlugs, application_variant_id: applicationVariantId, application_variant_ids: applicationVariantIds, application_variant_slug: applicationVariantSlug, application_variant_slugs: applicationVariantSlugs, name, description, flags, tags, meta, include_archived: includeArchived, next, newest, oldest, limit, order, } = request;
        const _queryParams = {
            application_id: applicationId,
            application_ids: applicationIds !== undefined ? toJson(applicationIds) : undefined,
            application_slug: applicationSlug,
            application_slugs: applicationSlugs !== undefined ? toJson(applicationSlugs) : undefined,
            application_variant_id: applicationVariantId,
            application_variant_ids: applicationVariantIds !== undefined ? toJson(applicationVariantIds) : undefined,
            application_variant_slug: applicationVariantSlug,
            application_variant_slugs: applicationVariantSlugs !== undefined ? toJson(applicationVariantSlugs) : undefined,
            name,
            description,
            flags,
            tags,
            meta,
            include_archived: includeArchived,
            next,
            newest: newest !== undefined ? newest : undefined,
            oldest: oldest !== undefined ? oldest : undefined,
            limit,
            order: order !== undefined ? order : undefined,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/variants/query"),
            method: "POST",
            headers: _headers,
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/variants/query");
    }
    /**
     * Fork an existing variant into a new variant on the same application.
     *
     * Use this to experiment without touching the source variant's history.
     * The fork copies the source variant's revisions up to the specified
     * revision (or tip) into the new variant, then commits the supplied
     * `revision` object on top. Both `variant` and `revision` sub-objects
     * in the request must be present; the server returns `count: 0` when
     * either is missing. Returns `400 Bad Request` if the fork target is
     * invalid (for example, the source variant or revision cannot be
     * located in this application's lineage).
     *
     * @param {AgentaApi.ApplicationVariantForkRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.forkApplicationVariant({
     *         application_variant: {},
     *         application_variant_ref: {}
     *     })
     */
    forkApplicationVariant(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__forkApplicationVariant(request, requestOptions));
    }
    async __forkApplicationVariant(request, requestOptions) {
        const { application_variant_id: applicationVariantId, ..._body } = request;
        const _queryParams = {
            application_variant_id: applicationVariantId,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/variants/fork"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/variants/fork");
    }
    /**
     * Retrieve one application revision by reference.
     *
     * Accepts application / variant / revision references for direct lookup,
     * or an environment reference (with optional `key`) to resolve the
     * currently-deployed revision in that environment. Returns the revision
     * including its `data` payload (URL, parameters, schemas), which clients
     * use to invoke the application.
     * Set `resolve: true` to inline embedded references inside `data`.
     *
     * @param {AgentaApi.ApplicationRevisionRetrieveRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.retrieveApplicationRevision()
     */
    retrieveApplicationRevision(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__retrieveApplicationRevision(request, requestOptions));
    }
    async __retrieveApplicationRevision(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/retrieve"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/retrieve");
    }
    /**
     * Deploy an application revision to an environment.
     *
     * Writes a reference from the environment revision to the application
     * revision under `key` (default: `{application_slug}.revision`). Clients
     * that subsequently call `/applications/revisions/retrieve` with the
     * same `environment_ref` and `key` resolve to this revision.
     * See the [Applications guide](/reference/api-guide/applications#deployment).
     *
     * @param {AgentaApi.ApplicationRevisionDeployRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.deployApplicationRevision()
     */
    deployApplicationRevision(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__deployApplicationRevision(request, requestOptions));
    }
    async __deployApplicationRevision(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/deploy"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/deploy");
    }
    /**
     * Create and commit the initial revision for an application variant.
     *
     * Advanced use only. For normal development loops prefer
     * `POST /applications/revisions/commit`, which commits the new revision
     * as the variant's tip and assigns a version number.
     *
     * @param {AgentaApi.ApplicationRevisionCreateRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.createApplicationRevision({
     *         application_revision: {}
     *     })
     */
    createApplicationRevision(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createApplicationRevision(request, requestOptions));
    }
    async __createApplicationRevision(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/");
    }
    /**
     * Fetch one revision by its ID.
     *
     * Returns the revision including its `data` payload. For lookup by
     * variant slug or environment, use `POST /applications/revisions/retrieve`.
     *
     * @param {AgentaApi.FetchApplicationRevisionRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.fetchApplicationRevision({
     *         application_revision_id: "application_revision_id"
     *     })
     */
    fetchApplicationRevision(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchApplicationRevision(request, requestOptions));
    }
    async __fetchApplicationRevision(request, requestOptions) {
        const { application_revision_id: applicationRevisionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/revisions/${core.url.encodePathParam(applicationRevisionId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/applications/revisions/{application_revision_id}");
    }
    /**
     * Edit a revision's header fields only.
     *
     * Revisions are immutable snapshots; `data`, `author`, `date`, and
     * `message` cannot be changed. This endpoint updates header fields such
     * as `description` and `tags`. To change configuration, commit a new
     * revision with `POST /applications/revisions/commit`.
     *
     * @param {AgentaApi.ApplicationRevisionEditRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.editApplicationRevision({
     *         application_revision_id: "application_revision_id",
     *         application_revision: {}
     *     })
     */
    editApplicationRevision(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editApplicationRevision(request, requestOptions));
    }
    async __editApplicationRevision(request, requestOptions) {
        const { application_revision_id: applicationRevisionId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/revisions/${core.url.encodePathParam(applicationRevisionId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/applications/revisions/{application_revision_id}");
    }
    /**
     * Soft-delete a revision.
     *
     * Archived revisions are hidden from `/query` and `/log` responses
     * unless `include_archived: true` is set. The ID remains resolvable for
     * traces and deployed environment references.
     *
     * @param {AgentaApi.ArchiveApplicationRevisionRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.archiveApplicationRevision({
     *         application_revision_id: "application_revision_id"
     *     })
     */
    archiveApplicationRevision(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__archiveApplicationRevision(request, requestOptions));
    }
    async __archiveApplicationRevision(request, requestOptions) {
        const { application_revision_id: applicationRevisionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/revisions/${core.url.encodePathParam(applicationRevisionId)}/archive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/{application_revision_id}/archive");
    }
    /**
     * Restore a previously archived revision.
     *
     * @param {AgentaApi.UnarchiveApplicationRevisionRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.unarchiveApplicationRevision({
     *         application_revision_id: "application_revision_id"
     *     })
     */
    unarchiveApplicationRevision(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__unarchiveApplicationRevision(request, requestOptions));
    }
    async __unarchiveApplicationRevision(request, requestOptions) {
        const { application_revision_id: applicationRevisionId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `applications/revisions/${core.url.encodePathParam(applicationRevisionId)}/unarchive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/{application_revision_id}/unarchive");
    }
    /**
     * Query revisions across one or more applications or variants.
     *
     * Use `application_refs` / `application_variant_refs` to scope the
     * query, or filter on commit metadata (`author`, `date`, `message`) via
     * the `application_revision` object. For the ordered history of a
     * single variant, `POST /applications/revisions/log` is more direct.
     *
     * @param {AgentaApi.ApplicationRevisionQueryRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.queryApplicationRevisions()
     */
    queryApplicationRevisions(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__queryApplicationRevisions(request, requestOptions));
    }
    async __queryApplicationRevisions(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/query");
    }
    /**
     * Commit a new revision on a variant.
     *
     * The new revision becomes the variant's tip and is assigned the next
     * `version` number. Revisions are immutable once committed; to change
     * configuration, commit a new revision.
     * See [Versioning](/reference/api-guide/versioning#committing-a-revision).
     *
     * @param {AgentaApi.ApplicationRevisionCommitRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.commitApplicationRevision({
     *         application_revision: {}
     *     })
     */
    commitApplicationRevision(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__commitApplicationRevision(request, requestOptions));
    }
    async __commitApplicationRevision(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/commit"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/commit");
    }
    /**
     * Return the ordered revision log for a variant.
     *
     * Pass `application_variant_id` to list the full history of that
     * variant; optionally pass `application_revision_id` + `depth` to walk
     * back a bounded number of commits from a specific revision. Entries
     * are returned newest-first and include the full revision record.
     *
     * @param {AgentaApi.ApplicationRevisionsLogRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.logApplicationRevisions({
     *         application_revisions: {}
     *     })
     */
    logApplicationRevisions(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__logApplicationRevisions(request, requestOptions));
    }
    async __logApplicationRevisions(request, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/log"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/log");
    }
    /**
     * Fetch a revision with embedded references inlined.
     *
     * When a revision's `data` carries references to other entities
     * (snippets, linked revisions), this endpoint resolves them in place and
     * returns the fully-inlined configuration along with `resolution_info`
     * describing what was substituted. Use it when clients need a
     * self-contained configuration for invocation or export.
     *
     * @param {AgentaApi.ApplicationRevisionResolveRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.resolveApplicationRevision()
     */
    resolveApplicationRevision(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__resolveApplicationRevision(request, requestOptions));
    }
    async __resolveApplicationRevision(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "applications/revisions/resolve"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return {
                data: _response.body,
                rawResponse: _response.rawResponse,
            };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/applications/revisions/resolve");
    }
    /**
     * Create an application end-to-end.
     *
     * Creates the application artifact, a default variant, and a first
     * committed revision whose `data` comes from the request. This is the
     * recommended entry point for "spin up a new application from a
     * template". For more control over variant and revision creation, use
     * the structured endpoints under `/applications/`.
     * See [Simple Endpoints](/reference/api-guide/simple-endpoints).
     *
     * @param {AgentaApi.SimpleApplicationCreateRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.createSimpleApplication({
     *         application: {}
     *     })
     */
    createSimpleApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__createSimpleApplication(request, requestOptions));
    }
    async __createSimpleApplication(request, requestOptions) {
        const { application_id: applicationId, ..._body } = request;
        const _queryParams = {
            application_id: applicationId,
        };
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "simple/applications/"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: { ..._queryParams, ...requestOptions?.queryParams },
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/simple/applications/");
    }
    /**
     * Query applications with variant, revision, and `data` merged per row.
     *
     * This is the shape most clients want for dashboards or invocation
     * pickers: each row carries `variant_id`, `revision_id`, and `data`
     * (URL, parameters, schemas) alongside the artifact fields. For the
     * structured query that returns artifacts only, use
     * `POST /applications/query`.
     *
     * @param {AgentaApi.SimpleApplicationQueryRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.querySimpleApplications()
     */
    querySimpleApplications(request = {}, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__querySimpleApplications(request, requestOptions));
    }
    async __querySimpleApplications(request = {}, requestOptions) {
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, "simple/applications/query"),
            method: "POST",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: request,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/simple/applications/query");
    }
    /**
     * Fetch one application with its current variant, revision, and `data` merged.
     *
     * The returned `data` includes the invocation `url`, the `parameters`
     * the revision was committed with, and the JSON `schemas` for inputs,
     * outputs, and parameters.
     *
     * @param {AgentaApi.FetchSimpleApplicationRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.fetchSimpleApplication({
     *         application_id: "application_id"
     *     })
     */
    fetchSimpleApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__fetchSimpleApplication(request, requestOptions));
    }
    async __fetchSimpleApplication(request, requestOptions) {
        const { application_id: applicationId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/applications/${core.url.encodePathParam(applicationId)}`),
            method: "GET",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "GET", "/simple/applications/{application_id}");
    }
    /**
     * Edit an application and commit a new revision if configuration changed.
     *
     * Fields other than `id` in the request body are treated as changes and
     * produce a new committed revision. Supplying `data` changes the
     * configuration; supplying only header fields (`flags`, `tags`, `meta`)
     * still produces a new revision with the updated header but the
     * existing `data`. Editing the application `name` is currently
     * disabled and returns `400`.
     *
     * @param {AgentaApi.SimpleApplicationEditRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.editSimpleApplication({
     *         application_id: "application_id",
     *         application: {}
     *     })
     */
    editSimpleApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__editSimpleApplication(request, requestOptions));
    }
    async __editSimpleApplication(request, requestOptions) {
        const { application_id: applicationId, ..._body } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/applications/${core.url.encodePathParam(applicationId)}`),
            method: "PUT",
            headers: _headers,
            contentType: "application/json",
            queryParameters: requestOptions?.queryParams,
            requestType: "json",
            body: _body,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "PUT", "/simple/applications/{application_id}");
    }
    /**
     * Archive an application through the simple endpoint layer.
     *
     * Equivalent to `POST /applications/{application_id}/archive`; returns
     * the archived application in the simple shape (with its last known
     * variant, revision, and `data`).
     *
     * @param {AgentaApi.ArchiveSimpleApplicationRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.archiveSimpleApplication({
     *         application_id: "application_id"
     *     })
     */
    archiveSimpleApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__archiveSimpleApplication(request, requestOptions));
    }
    async __archiveSimpleApplication(request, requestOptions) {
        const { application_id: applicationId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/applications/${core.url.encodePathParam(applicationId)}/archive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/simple/applications/{application_id}/archive");
    }
    /**
     * Unarchive an application through the simple endpoint layer.
     *
     * Equivalent to `POST /applications/{application_id}/unarchive`, with
     * the response shape of `/simple/applications/`.
     *
     * @param {AgentaApi.UnarchiveSimpleApplicationRequest} request
     * @param {ApplicationsClient.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link AgentaApi.UnprocessableEntityError}
     *
     * @example
     *     await client.applications.unarchiveSimpleApplication({
     *         application_id: "application_id"
     *     })
     */
    unarchiveSimpleApplication(request, requestOptions) {
        return core.HttpResponsePromise.fromPromise(this.__unarchiveSimpleApplication(request, requestOptions));
    }
    async __unarchiveSimpleApplication(request, requestOptions) {
        const { application_id: applicationId } = request;
        const _authRequest = await this._options.authProvider.getAuthRequest();
        const _headers = mergeHeaders(_authRequest.headers, this._options?.headers, requestOptions?.headers);
        const _response = await core.fetcher({
            url: core.url.join((await core.Supplier.get(this._options.baseUrl)) ??
                (await core.Supplier.get(this._options.environment)) ??
                environments.AgentaApiEnvironment.Default, `simple/applications/${core.url.encodePathParam(applicationId)}/unarchive`),
            method: "POST",
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: (requestOptions?.timeoutInSeconds ?? this._options?.timeoutInSeconds ?? 30) * 1000,
            maxRetries: requestOptions?.maxRetries ?? this._options?.maxRetries,
            withCredentials: true,
            abortSignal: requestOptions?.abortSignal,
            fetchFn: this._options?.fetch,
            logging: this._options.logging,
        });
        if (_response.ok) {
            return { data: _response.body, rawResponse: _response.rawResponse };
        }
        if (_response.error.reason === "status-code") {
            switch (_response.error.statusCode) {
                case 422:
                    throw new AgentaApi.UnprocessableEntityError(_response.error.body, _response.rawResponse);
                default:
                    throw new errors.AgentaApiError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }
        return handleNonStatusCodeError(_response.error, _response.rawResponse, "POST", "/simple/applications/{application_id}/unarchive");
    }
}
